import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Wand2, Loader2, X, RefreshCw, Check } from "lucide-react";
import aiService from "../services/aiService";
import { useTranslation } from "../i18n";

const TONES = ["warm", "professional", "playful"];

const escapeHtml = (s) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Plain-text draft -> editor HTML, one <p> per paragraph. */
const toHtml = (text) =>
  text
    .split(/\n\s*\n/)
    .map((para) => para.trim())
    .filter(Boolean)
    .map((para) => `<p>${escapeHtml(para).replace(/\n/g, "<br>")}</p>`)
    .join("");

/**
 * "Help me write" for the recognition message editor.
 *
 * The sender jots a few notes (the current message is used as a starting
 * point), picks a tone, and gets a draft back. Nothing touches the editor
 * until they choose "Use this"; the draft can be regenerated first.
 * Renders nothing when the backend has AI turned off.
 *
 * Props: editor (Tiptap), recipientIds, recognitionTypeId, onApply(html).
 */
const AiComposePanel = ({ editor, recipientIds, recognitionTypeId, onApply }) => {
  const { t } = useTranslation("recognition");
  const { data: status } = useQuery({
    queryKey: ["ai-status"],
    queryFn: aiService.getStatus,
    staleTime: Infinity,
    retry: false,
  });
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [tone, setTone] = useState("warm");
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!status?.enabled) return null;

  const openPanel = () => {
    // Start from whatever the sender has already typed.
    setNotes(editor?.getText().trim() || "");
    setDraft("");
    setError("");
    setOpen(true);
  };

  const generate = async () => {
    setLoading(true);
    setError("");
    try {
      const { message } = await aiService.composeRecognition({
        recipientIds,
        recognitionTypeId,
        notes,
        tone,
      });
      setDraft(message);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 429) setError(t("ai_compose.errors.rate_limited"));
      else if (code === 422) setError(t("ai_compose.errors.declined"));
      else setError(t("ai_compose.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    onApply(toHtml(draft));
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={openPanel}
        className="ml-auto flex items-center gap-1 px-2 py-1 rounded text-sm text-indigo-600 hover:bg-indigo-50"
      >
        <Wand2 className="w-4 h-4" />
        {t("ai_compose.open")}
      </button>
    );
  }

  let generateLabel = t("ai_compose.generate");
  if (loading) generateLabel = t("ai_compose.generating");
  else if (draft) generateLabel = t("ai_compose.regenerate");

  return (
    <div className="basis-full w-full my-1 rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1 text-sm font-medium text-indigo-700">
          <Wand2 className="w-4 h-4" />
          {t("ai_compose.title")}
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label={t("ai_compose.close")}
          className="p-1 rounded hover:bg-indigo-100 text-gray-500"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        maxLength={2000}
        rows={3}
        placeholder={t("ai_compose.notes_placeholder")}
        className="w-full rounded-md border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
      />

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-600">{t("ai_compose.tone_label")}</span>
        {TONES.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTone(key)}
            className={`px-2.5 py-1 rounded-full border text-xs ${
              tone === key
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white text-gray-700 border-gray-300 hover:border-indigo-300"
            }`}
          >
            {t(`ai_compose.tones.${key}`)}
          </button>
        ))}
        <button
          type="button"
          onClick={generate}
          disabled={loading}
          className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-md bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-60"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : draft ? (
            <RefreshCw className="w-4 h-4" />
          ) : (
            <Wand2 className="w-4 h-4" />
          )}
          {generateLabel}
        </button>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {draft && (
        <div className="rounded-md border border-gray-200 bg-white p-3">
          <p className="text-sm text-gray-800 whitespace-pre-line">{draft}</p>
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-xs text-gray-500">{t("ai_compose.review_hint")}</span>
            <button
              type="button"
              onClick={apply}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-green-600 text-white text-sm hover:bg-green-700"
            >
              <Check className="w-4 h-4" />
              {t("ai_compose.use_this")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiComposePanel;
